import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Button, Chip, Collapse, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { payrollAPI } from '../../services/api';
import { gapLabel, statuteLabel } from './plainLabels';
import { PageHeader, Section } from './payrollUi';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function PayMonthPage() {
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const monthQuery = useQuery(['pay-month', year, month], async () => {
    const res = await payrollAPI.getMonth(year, month);
    return res.data.data;
  });
  const peopleQuery = useQuery(['pay-people'], async () => (await payrollAPI.listPeople()).data.data);

  const onOk = (ok) => ({
    onSuccess: () => {
      qc.invalidateQueries(['pay-month']);
      toast.success(ok);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Request failed'),
  });
  const openMut = useMutation(() => payrollAPI.openMonth(year, month), onOk('Month opened'));
  const generateMut = useMutation(() => payrollAPI.generateMonth(year, month), onOk('Month generated'));
  const processMut = useMutation(() => payrollAPI.processMonth(year, month), onOk('Month processed'));
  const reopenMut = useMutation(() => payrollAPI.reopenMonth(year, month), onOk('Month reopened'));

  const data = monthQuery.data;
  const incomplete = (peopleQuery.data || []).filter((p) => !p.complete);
  const paidPeople = data?.people || [];
  const status = data?.run?.status;
  const monthLabel = `${MONTHS[month - 1] || 'Month'} ${year}`;
  let statusText = `${monthLabel} has not been started.`;
  if (status === 'processed') statusText = `${monthLabel} is finished.`;
  else if (status && paidPeople.length === 0) statusText = `${monthLabel} is started. Work out the pay next.`;
  else if (status) statusText = `${monthLabel} pay is worked out. Check the amounts, then finish the month.`;

  const downloadCsv = () => payrollAPI.downloadCsv(year, month).then((res) => {
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pay-${year}-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  });

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Payroll"
        subtitle="Choose the month. Anyone missing details is listed below and is left out until those details are filled in."
        actions={status && <Chip label={status === 'processed' ? 'Finished' : 'In progress'} color={status === 'processed' ? 'success' : 'default'} />}
      />
      <Section title={monthLabel}>
        <Typography variant="body1" sx={{ mb: 2 }}>{statusText}</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ minWidth: 160 }}>
            {MONTHS.map((name, index) => <MenuItem key={name} value={index + 1}>{name}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }} />
          <Box sx={{ flex: 1 }} />
          {!status && <Button variant="contained" onClick={() => openMut.mutate()} disabled={openMut.isLoading}>Start this month</Button>}
          {status && status !== 'processed' && paidPeople.length === 0 && (
            <Button variant="contained" onClick={() => generateMut.mutate()} disabled={generateMut.isLoading}>Work out pay</Button>
          )}
          {status && status !== 'processed' && paidPeople.length > 0 && (
            <>
              <Button variant="outlined" onClick={() => generateMut.mutate()} disabled={generateMut.isLoading}>Work out pay again</Button>
              <Button variant="contained" onClick={() => processMut.mutate()} disabled={processMut.isLoading}>Finish this month</Button>
            </>
          )}
          {status === 'processed' && (
            <>
              <Button variant="contained" onClick={downloadCsv}>Download for the accountant</Button>
              <Button variant="outlined" onClick={() => reopenMut.mutate()} disabled={reopenMut.isLoading}>Open this month again</Button>
            </>
          )}
        </Stack>
      </Section>
      {incomplete.length > 0 && <NotReady people={incomplete} />}
      {(data?.ruleReviews || []).length > 0 && (
        <Section title="Rules due for a look" hint="Nothing changes by itself. Open Pay setup if a figure needs a new date.">
          {(data.ruleReviews || []).map((row) => (
            <Typography key={row.item_key} variant="body2" sx={{ mb: 0.5 }}>
              {row.item_label}{row.figureInUse ? ` · ${row.figureInUse}` : ''}
            </Typography>
          ))}
        </Section>
      )}
      {(data?.exits || []).length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">Exit runs</Typography>
          <Typography variant="body2">A person who leaves this month is paid here, up to the exit date. They are left out of the ordinary month so the same days are not paid twice. Leave encashment, gratuity, and statutory bonus stay off this run.</Typography>
          {data.exits.map((person) => (
            <Stack key={person.id} direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2">{person.name} · exit {String(person.exit_date).slice(0, 10)} · {person.exitRun?.status || 'not opened'}{person.complete ? '' : ' · profile incomplete'}</Typography>
              <Button size="small" disabled={!person.complete || person.exitRun?.status === 'processed'} onClick={() => payrollAPI.generateExit(year, month, person.id).then(() => qc.invalidateQueries(['pay-month']))}>Generate exit</Button>
              <Button size="small" disabled={person.exitRun?.status !== 'open'} onClick={() => payrollAPI.processExit(year, month, person.id).then(() => qc.invalidateQueries(['pay-month']))}>Process exit</Button>
            </Stack>
          ))}
        </Paper>
      )}
      {(data?.waitingLeave || []).length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">Leave still waiting</Typography>
          <Typography variant="body2">These requests are not approved, so they are not pay yet. They do not stop the month.</Typography>
          {data.waitingLeave.map((row) => (
            <Typography key={row.id} variant="body2">
              {row.name}: {row.leave_type_name} {String(row.start_date).slice(0, 10)} to {String(row.end_date).slice(0, 10)} ({row.day_span})
            </Typography>
          ))}
        </Paper>
      )}
      {(data?.weeks || []).length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">Submitted weeks</Typography>
          {data.weeks.map((week) => (
            <Stack key={week.id} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {week.name} {String(week.week_start).slice(0, 10)} {week.counted == null ? 'not decided' : week.counted ? 'counted' : 'not counted'}
              </Typography>
              <Button size="small" onClick={() => payrollAPI.markWeek(year, month, week.id, { counted: true }).then(() => qc.invalidateQueries(['pay-month']))}>Count</Button>
              <Button size="small" onClick={() => payrollAPI.markWeek(year, month, week.id, { counted: false }).then(() => qc.invalidateQueries(['pay-month']))}>Do not count</Button>
            </Stack>
          ))}
        </Paper>
      )}
      {paidPeople.length > 0 && (
        <PayTable
          people={paidPeople}
          year={year}
          month={month}
          onRefresh={() => qc.invalidateQueries(['pay-month'])}
          onExtend={(userId, incentiveId, endDate) => payrollAPI.extendIncentive(userId, incentiveId, { endDate }).then(() => generateMut.mutate())}
        />
      )}
    </Stack>
  );
}

function NotReady({ people }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? people.filter((person) => String(person.name || '').toLowerCase().includes(q)) : people;
  return (
    <Section
      title={`${people.length} people cannot be paid yet`}
      hint="Each row is one person and what is still missing. Fill that in, then come back and work out the pay."
    >
      <TextField size="small" fullWidth placeholder="Search by name" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ mb: 2, maxWidth: 320 }} />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Person</TableCell>
            <TableCell>Still needed</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((person) => (
            <TableRow key={person.id}>
              <TableCell>{person.name}</TableCell>
              <TableCell>{(person.missing || []).map(gapLabel).join(', ')}</TableCell>
              <TableCell align="right">
                <Button size="small" component={RouterLink} to={`/payroll/people?person=${person.id}`}>Fill in</Button>
              </TableCell>
            </TableRow>
          ))}
          {shown.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>No one matches that name.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

function money(value) {
  if (value == null || value === '') return '—';
  return `₹${Number(value).toLocaleString('en-IN')}`;
}

function PayTable({ people, year, month, onRefresh, onExtend }) {
  const [openId, setOpenId] = useState(null);
  return (
    <Section title="This month" hint="Click a person to mark a day with no hours, or type a tax or contribution amount.">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Person</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Present days</TableCell>
            <TableCell align="right">Before deductions</TableCell>
            <TableCell align="right">They receive</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {people.map((person) => {
            const open = openId === person.id;
            const unmarked = (person.days || []).filter((d) => d.kind === 'unmarked' && d.marked_paid == null);
            return (
              <React.Fragment key={person.id}>
                <TableRow hover selected={open} onClick={() => setOpenId(open ? null : person.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{person.display_name}</TableCell>
                  <TableCell>{person.payee_type === 'contractor' ? 'Contractor' : 'Employee'}</TableCell>
                  <TableCell align="right">{person.present_days ?? '—'}</TableCell>
                  <TableCell align="right">{money(person.gross)}</TableCell>
                  <TableCell align="right">{money(person.net)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
                    <Collapse in={open} unmountOnExit>
                      <Stack spacing={1} sx={{ py: 1.5 }}>
                        {(person.issues || []).map((issue, index) => (
                          <Stack key={index} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{issue.message}</Typography>
                            {issue.incentiveId && (
                              <Button size="small" onClick={() => {
                                const endDate = window.prompt('New end date (YYYY-MM-DD)');
                                if (endDate) onExtend(person.user_id, issue.incentiveId, endDate);
                              }}>Extend</Button>
                            )}
                          </Stack>
                        ))}
                        {unmarked.map((day) => (
                          <Stack key={day.work_date} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{String(day.work_date).slice(0, 10)} has no hours</Typography>
                            <Button size="small" onClick={() => payrollAPI.markDay(year, month, person.user_id, { date: String(day.work_date).slice(0, 10), paid: true }).then(onRefresh)}>Paid</Button>
                            <Button size="small" onClick={() => payrollAPI.markDay(year, month, person.user_id, { date: String(day.work_date).slice(0, 10), paid: false }).then(onRefresh)}>Not paid</Button>
                          </Stack>
                        ))}
                        {(person.statutes || []).filter((row) => row.status !== 'not_applicable').map((row) => (
                          <Typography key={row.id} variant="body2">
                            {statuteLabel(row.statute_code)}: from them {money(row.employee_amount)} · from Bacchapanti {money(row.employer_amount)}
                          </Typography>
                        ))}
                        <ManualAmount year={year} month={month} userId={person.user_id} onDone={onRefresh} />
                      </Stack>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Section>
  );
}

const MANUAL_CODES = ['pf', 'esi', 'pt', 'lwf', 'tds_salary', 'tds_professional_fee', 'gratuity', 'bonus'];

function ManualAmount({ year, month, userId, onDone }) {
  const [statuteCode, setStatuteCode] = useState('pf');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
      <TextField select size="small" label="Which one" value={statuteCode} onChange={(e) => setStatuteCode(e.target.value)} sx={{ minWidth: 200 }}>
        {MANUAL_CODES.map((code) => <MenuItem key={code} value={code}>{statuteLabel(code)}</MenuItem>)}
      </TextField>
      <TextField size="small" label="Rupees" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <TextField size="small" label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Button size="small" onClick={() => payrollAPI.setManualStatute(year, month, userId, {
        statuteCode, amount: Number(amount), reason,
      }).then(onDone)}>Save this amount</Button>
    </Stack>
  );
}
